import type { SelectChangeEvent } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getClientData } from '../../services/adminService';
import {
  getUsers,
  inviteUser,
  resetPassword,
  revokeInvitation,
  type UpdateUserData,
  updateInvitation,
  updateUser,
} from '../../services/api';
import { getBillingLedger } from '../../services/billingService';
import { getTeam, getTeamsByClient } from '../../services/teamService';
import { getUser, getUserToken } from '../../services/userService';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { getPermissionsForRole } from '../../shared/constants/roles';
import type { User, UserRole } from '../../types/auth';
import type { Team } from '../../types/team';
import type { UserLimits } from '../../types/user';
import type { DisplayUser, EditUserFormData } from './types';

const EMPTY_OBJECT: Record<string, never> = {};

export const useUserDetails = (userId: string | undefined) => {
  const navigate = useNavigate();
  const { hasPermission } = useAccount();
  const { showSnackbar } = useSnackbar();

  const [user, setUser] = useState<DisplayUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState<EditUserFormData>({
    displayName: '',
    role: 'user',
    clientId: '',
    teamId: '',
    disabled: false,
    billingType: 'organization-managed',
    limits: {
      numTenders: 0,
      numDrafts: 0,
      numReviews: 0,
      numReports: 0,
      numAdHocDocs: 0,
      numSectionChats: 0,
    },
    permissions: [],
    isCustomPermissions: false,
  });

  // Context Data
  const [teams, setTeams] = useState<Pick<Team, 'id' | 'teamName' | 'limits'>[]>([]);
  const [currentPoolLimits, setCurrentPoolLimits] = useState<UserLimits | null>(null);
  const [peerUsersLimits, setPeerUsersLimits] = useState<UserLimits>({
    numTenders: 0,
    numDrafts: 0,
    numReviews: 0,
    numReports: 0,
    numAdHocDocs: 0,
    numSectionChats: 0,
  });

  // Dialogs
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState<{
    title: string;
    description: string;
    onConfirm?: () => void;
  }>({
    title: '',
    description: '',
  });

  const canEditUser = hasPermission(PERMISSIONS.USER_UPDATE);
  const canManageRoles = hasPermission(PERMISSIONS.USER_MANAGE_ROLES);
  const canManageSystem = hasPermission(PERMISSIONS.SYSTEM_MANAGE);

  const fetchUserData = useCallback(async () => {
    if (!userId) {
      return;
    }
    setLoading(true);
    try {
      const displayUser = (await getUser(userId)) as DisplayUser | null;
      if (displayUser) {
        setUser(displayUser);

        // Initialize Form Data
        const rolePerms = getPermissionsForRole(displayUser.role || 'user');
        const userPerms = displayUser.cachedPermissions || rolePerms;
        const isCustom =
          userPerms.length !== rolePerms.length ||
          !userPerms.every((p: string) => rolePerms.includes(p));

        setFormData({
          displayName: displayUser.displayName || '',
          role: displayUser.role || 'user',
          clientId: displayUser.clientId || '',
          teamId: displayUser.teamId || '',
          disabled: displayUser.disabled || false,
          billingType: displayUser.billingType || 'organization-managed',
          limits: displayUser.limits || {
            numTenders: 0,
            numDrafts: 0,
            numReviews: 0,
            numReports: 0,
            numAdHocDocs: 0,
            numSectionChats: 0,
          },
          permissions: userPerms,
          isCustomPermissions: isCustom,
        });
      } else {
        // Try fetching from userTokens
        const tokenData = await getUserToken(userId);
        if (tokenData) {
          const displayUser: DisplayUser = {
            uid: tokenData.uid,
            email: tokenData.email || tokenData.invitedUserEmail || '',
            displayName: tokenData.name || tokenData.invitedByName || '',
            role: (tokenData.role as UserRole) || 'user',
            clientId: tokenData.clientId || '',
            teamId: tokenData.teamAssignments?.[0] || '',
            disabled: true,
            status: 'pending' as const,
          };
          setUser(displayUser);

          // Initialize Form Data
          const rolePerms = getPermissionsForRole(displayUser.role || 'user');
          const userPerms = (tokenData.permissions as string[]) || rolePerms;
          const isCustom =
            userPerms.length !== rolePerms.length ||
            !userPerms.every((p: string) => rolePerms.includes(p));

          setFormData({
            displayName: displayUser.displayName || '',
            role: displayUser.role || 'user',
            clientId: displayUser.clientId || '',
            teamId: displayUser.teamId || '',
            disabled: displayUser.disabled || false,
            billingType: 'organization-managed',
            limits: (tokenData.limits as unknown as UserLimits) || {
              numTenders: 0,
              numDrafts: 0,
              numReviews: 0,
              numReports: 0,
              numAdHocDocs: 0,
              numSectionChats: 0,
            },
            permissions: userPerms,
            isCustomPermissions: isCustom,
          });
        } else {
          showSnackbar('User not found', 'error');
          navigate(-1);
        }
      }
    } catch (_err) {
      showSnackbar('Failed to load user data', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate, showSnackbar]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Fetch Teams
  useEffect(() => {
    const fetchTeams = async () => {
      const targetClientId = formData.clientId || user?.clientId;
      if (targetClientId) {
        try {
          const teamsData = await getTeamsByClient(targetClientId);
          setTeams(teamsData);
        } catch (_err) {
          // Silent
        }
      }
    };
    fetchTeams();
  }, [formData.clientId, user?.clientId]);

  // Calculate Limits Context
  useEffect(() => {
    const calculateContext = async () => {
      const targetClientId = formData.clientId || user?.clientId;
      const targetTeamId = formData.teamId;

      if (!targetClientId) {
        return;
      }

      try {
        let poolLimits: UserLimits;

        // 1. Pool Limits
        if (targetTeamId) {
          const team = teams.find((t) => t.id === targetTeamId);
          let teamLimitsRaw = team?.limits;
          if (!teamLimitsRaw) {
            try {
              const teamData = await getTeam(targetTeamId);
              if (teamData) {
                teamLimitsRaw = teamData.limits;
              }
            } catch (_e) {
              // Ignore
            }
          }
          poolLimits = (teamLimitsRaw as UserLimits) || {
            numTenders: 0,
            numDrafts: 0,
            numReviews: 0,
            numReports: 0,
            numAdHocDocs: 0,
            numSectionChats: 0,
          };
          for (const key of Object.keys(poolLimits) as (keyof UserLimits)[]) {
            if (poolLimits[key] === Infinity) {
              poolLimits[key] = -1;
            }
          }
        } else {
          try {
            const clientData = await getClientData(targetClientId);
            const ledgerData = await getBillingLedger(targetClientId);

            const isPayg = ledgerData?.isPAYG ?? clientData?.subscription?.billing_model === 'payg';

            if (isPayg) {
              poolLimits = {
                numTenders: -1,
                numDrafts: -1,
                numReviews: -1,
                numReports: -1,
                numAdHocDocs: -1,
                numSectionChats: -1,
              };
            } else {
              poolLimits = (clientData?.subscription?.limits as UserLimits) || {
                numTenders: 0,
                numDrafts: 0,
                numReviews: 0,
                numReports: 0,
                numAdHocDocs: 0,
                numSectionChats: 0,
              };
            }
          } catch (_e) {
            poolLimits = {
              numTenders: 0,
              numDrafts: 0,
              numReviews: 0,
              numReports: 0,
              numAdHocDocs: 0,
              numSectionChats: 0,
            };
          }
        }
        setCurrentPoolLimits(poolLimits);

        // 2. Peer Usage
        const response = await getUsers({ companyId: targetClientId });
        if (response.success && response.users) {
          const totals: UserLimits = {
            numTenders: 0,
            numDrafts: 0,
            numReviews: 0,
            numReports: 0,
            numAdHocDocs: 0,
            numSectionChats: 0,
          };
          const relevantUsers = response.users.filter((u: User) => {
            if (targetTeamId) {
              return u.teamId === targetTeamId;
            }
            return true;
          });

          for (const u of relevantUsers) {
            if (u.uid === user?.uid) {
              continue;
            }
            if (targetTeamId && u.teamId !== targetTeamId) {
              continue;
            }
            const l = (u.limits as UserLimits) || EMPTY_OBJECT;
            totals.numTenders = (totals.numTenders || 0) + (l.numTenders || 0);
            totals.numDrafts = (totals.numDrafts || 0) + (l.numDrafts || 0);
            totals.numReviews = (totals.numReviews || 0) + (l.numReviews || 0);
            totals.numReports = (totals.numReports || 0) + (l.numReports || 0);
            totals.numAdHocDocs = (totals.numAdHocDocs || 0) + (l.numAdHocDocs || 0);
            totals.numSectionChats = (totals.numSectionChats || 0) + (l.numSectionChats || 0);
          }
          setPeerUsersLimits(totals);
        }
      } catch (_err) {
        // Ignore
      }
    };

    if (!loading) {
      calculateContext();
    }
  }, [formData.clientId, formData.teamId, user?.clientId, user?.uid, loading, teams]);

  const getAvailability = useCallback(
    (key: keyof UserLimits) => {
      let max = currentPoolLimits?.[key] ?? 0;
      if (max === Infinity) {
        max = -1;
      }
      const usedByOthers = peerUsersLimits[key] || 0;
      const available = max === -1 ? Infinity : Math.max(0, max - usedByOthers);
      return { max, usedByOthers, available };
    },
    [currentPoolLimits, peerUsersLimits]
  );

  // Input Handlers
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  const handleLimitChange = useCallback((key: keyof UserLimits, value: string) => {
    const parsedValue = parseInt(value, 10);
    setFormData((prev) => ({
      ...prev,
      limits: {
        ...prev.limits,
        [key]: Number.isNaN(parsedValue) ? 0 : parsedValue,
      },
    }));
  }, []);

  const handleSelectChange = useCallback((event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (name === 'role') {
      setFormData((prev) => ({
        ...prev,
        role: value as UserRole,
        permissions: getPermissionsForRole(value as string),
        isCustomPermissions: false,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }, []);

  const handlePermissionToggle = useCallback((perm: string) => {
    setFormData((prev) => {
      const newPerms = prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm];
      return {
        ...prev,
        permissions: newPerms,
        isCustomPermissions: true,
      };
    });
  }, []);

  const handleCustomSwitch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const isCustom = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      isCustomPermissions: isCustom,
      permissions: isCustom ? prev.permissions : getPermissionsForRole(prev.role),
    }));
  }, []);

  // Actions
  const handleSave = useCallback(async () => {
    if (!user) {
      return;
    }
    if ((formData.displayName || '').length > 50) {
      setError('Display name cannot exceed 50 characters.');
      return;
    }
    if ((formData.clientId || '').length > 100) {
      setError('Client ID cannot exceed 100 characters.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const { permissions, isCustomPermissions, ...restFormData } = formData;

      if (user.status === 'pending') {
        const payload: {
          role?: string;
          teamId?: string;
          permissions?: string[];
          limits?: UserLimits;
        } = {
          role: formData.role,
          teamId: formData.teamId,
          limits: formData.limits,
        };
        if (isCustomPermissions) {
          payload.permissions = permissions;
        }

        await updateInvitation(user.uid, payload);
        showSnackbar('Invitation updated successfully', 'success');
        fetchUserData();
      } else {
        const updatePayload: UpdateUserData = {
          userId: user.uid,
          ...restFormData,
          teamId: formData.teamId,
        };

        if (isCustomPermissions) {
          updatePayload.permissions = permissions;
        }

        await updateUser(updatePayload);
        showSnackbar('User updated successfully', 'success');
        fetchUserData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
      showSnackbar('Failed to update user', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [user, formData, showSnackbar, fetchUserData]);

  const handleResetPassword = useCallback(() => {
    if (!user) {
      return;
    }
    setConfirmationConfig({
      title: 'Reset Password',
      description: `Generate password reset link for ${user.email}? (Link will be logged, not emailed automatically)`,
      onConfirm: async () => {
        setIsConfirming(true);
        try {
          await resetPassword(user.uid);
          showSnackbar('Password reset initiated successfully.', 'success');
        } catch (error: unknown) {
          const err = error as Error;
          showSnackbar(err.message || 'Failed to reset password', 'error');
        } finally {
          setIsConfirming(false);
          setConfirmationDialogOpen(false);
        }
      },
    });
    setConfirmationDialogOpen(true);
  }, [user, showSnackbar]);

  const handleResendInvitation = useCallback(() => {
    if (!user) {
      return;
    }
    setConfirmationConfig({
      title: 'Resend Invitation',
      description: `Revoke old invitation and send a new one to ${user.email}?`,
      onConfirm: async () => {
        setIsConfirming(true);
        try {
          await revokeInvitation(user.uid);

          const payload: {
            email: string;
            role: string;
            companyId?: string;
            displayName: string;
            teamAssignments?: string[];
            permissions?: string[];
            limits?: UserLimits;
          } = {
            email: user.email || '',
            role: formData.role,
            displayName: formData.displayName || user.displayName || 'User',
            teamAssignments: formData.teamId ? [formData.teamId] : [],
            limits: formData.limits,
          };

          const targetCompanyId = formData.clientId || user.clientId;
          if (targetCompanyId) {
            payload.companyId = targetCompanyId;
          }

          if (formData.isCustomPermissions) {
            payload.permissions = formData.permissions;
          }

          await inviteUser(payload);
          showSnackbar('Invitation re-sent successfully.', 'success');
          navigate(-1);
        } catch (error: unknown) {
          const err = error as Error;
          showSnackbar(err.message || 'Failed to resend invitation', 'error');
        } finally {
          setIsConfirming(false);
          setConfirmationDialogOpen(false);
        }
      },
    });
    setConfirmationDialogOpen(true);
  }, [user, formData, navigate, showSnackbar]);

  return {
    user,
    loading,
    isSaving,
    error,
    formData,
    teams,
    canEditUser,
    canManageRoles,
    canManageSystem,
    confirmationDialogOpen,
    isConfirming,
    confirmationConfig,
    setConfirmationDialogOpen,
    getAvailability,
    handleChange,
    handleLimitChange,
    handleSelectChange,
    handlePermissionToggle,
    handleCustomSwitch,
    handleSave,
    handleResetPassword,
    handleResendInvitation,
    navigate,
  };
};
